import { useState, useEffect, useRef } from "react";
import {
  Autocomplete,
  DatePicker,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from "../../../../components";
import {
  AtivoServicoService,
  OrdemServicosStatusPersonalizadosService,
  PessoaService,
  SessionService,
} from "../../../../services";
import { value } from "../../../../helpers";
import { responseErros, timeoutBuscaAutocomplete } from "../../../../utils";
import { useFiltrosRelatoriosHandlers } from "../../components/use-filtros-relatorios-handlers";

const pessoaService = new PessoaService();
const ativoServicoService = new AtivoServicoService();
const ordemServicosStatusPersonalizadosService =
  new OrdemServicosStatusPersonalizadosService();

const FormFiltroAvancado = ({
  filtrosAvancados,
  setFiltrosAvancados,
  filtrosAplicadosImpressao,
  setFiltrosAplicadosImpressao,
  setValidationError,
}) => {
  const inputRefCliente = useRef();
  const [clienteList, setClienteList] = useState([]);
  const [vendedorList, setVendedorList] = useState([]);
  const [filteredVendedorList, setFilteredVendedorList] = useState([]);
  const [prestadorList, setPrestadorList] = useState([]);
  const [filteredPrestadorList, setFilteredPrestadorList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [ativoList, setAtivoList] = useState([]);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);
  // const [inputErro, setInputErro] = useState([]);
  // const inputErros = new InputErros(inputErro, setInputErro);
  const {
    onChangeInput,
    onChangeAutocomplete,
    onChangeDate,
    onChangeCheckbox,
  } = useFiltrosRelatoriosHandlers(
    setFiltrosAvancados,
    setFiltrosAplicadosImpressao,
  );

  const sessionService = new SessionService();
  const usuario = sessionService.getUsuario();
  const empresa = sessionService.getEmpresa();

  const onChangeSelectStatus = (event, label) => {
    resetarFiltrosChave("status");
    const { value, name } = event.target;
    const isNumber = Number.isInteger(value);
    const filterKey = isNumber ? "statusPersonalizadoId" : name;
    const selectedStatus = statusList.find((s) => s.id === value);
    setFiltrosAvancados((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
    setFiltrosAplicadosImpressao((prev) => ({
      ...prev,
      [label]: isNumber ? selectedStatus?.descricao : value,
    }));
  };

  useEffect(() => {
    setEstadoInicialFiltros();
    buscarVendedores();
    buscarPrestadores();
    buscarStatusPersonalizados();
    buscarAtivos();
  }, []);

  function setEstadoInicialFiltros() {
    const filtros = {
      ...filtrosAvancados,
      empresasIds: [empresa.id],
    };
    let filtrosImpressao = {};
    if (usuario?.acessosEmpresas?.length) {
      filtrosImpressao = {
        ...filtrosAplicadosImpressao,
        ["Empresas-Associadas"]: empresa.razaoSocial,
      };
    }
    if (!filtros?.canceladas) {
      filtros.canceladas = "false";
      filtrosImpressao["OS-Canceladas"] = "Não";
    }
    setFiltrosAvancados(filtros);
    setFiltrosAplicadosImpressao(filtrosImpressao);
  }

  const buscarClientes = () => {
    const filtros = {
      nonPaginated: true,
      tiposPessoaId: 1,
    };
    pessoaService.getAll(filtros).then((result) => {
      if (!result.isAxiosError) {
        setClienteList(result.data);
      } else {
        responseErros(result);
      }
    });
    setLoadingAutoComplete(false);
  };

  const buscarVendedores = () => {
    const filtro = {
      nonPaginated: true,
      vendedor: true,
      tiposPessoaId: 4,
    };
    pessoaService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setVendedorList(result.data);
        setFilteredVendedorList(result.data);
      } else {
        responseErros(result);
      }
    });
    setLoadingAutoComplete(false);
  };

  const buscarPrestadores = () => {
    const filtro = {
      nonPaginated: true,
      tiposPessoaId: 5,
    };
    pessoaService.getAll(filtro).then((result) => {
      if (!result.isAxiosError) {
        setPrestadorList(result.data);
        setFilteredPrestadorList(result.data);
      } else {
        responseErros(result);
      }
    });
    setLoadingAutoComplete(false);
  };

  const buscarAtivos = () => {
    ativoServicoService.getAll().then((result) => {
      if (!result.isAxiosError) {
        const ativosComCampoChave = (result.data || []).filter(
          (ativo) =>
            ativo?.ativado &&
            ativo?.camposPersonalizados?.some((campo) => campo.campoChave),
        );
        setAtivoList(ativosComCampoChave);
      } else {
        responseErros(result);
      }
    });
  };

  const ativoSelecionado = ativoList.find(
    ({ id }) => id === filtrosAvancados?.ativoId,
  );
  const campoChaveAtivo = ativoSelecionado?.camposPersonalizados?.find(
    (campo) => campo.campoChave,
  );

  const onChangeAtivo = (ativo) => {
    if (!ativo) {
      resetarFiltrosChave("ativoId");
      return;
    }
    onChangeAutocomplete("ativoId", ativo, "Ativo");
  };

  const onChangeCampoChaveAtivo = (valorCampoChave) => {
    const valorFormatado = valorCampoChave?.toUpperCase();
    if (!valorFormatado) {
      resetarFiltrosChave("ativoCampoChave");
      return;
    }
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      ativoCampoChave: valorFormatado,
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      ["Campo-Chave"]: valorFormatado,
    }));
  };

  const onInputChangePessoa = (event, tiposPessoaId) => {
    const inputRefMap = {
      1: {
        inputRef: inputRefCliente,
        buscar: buscarClientes,
      },
    };
    const { inputRef, buscar } = inputRefMap[tiposPessoaId] || {};
    if (
      event.key === "Enter" &&
      inputRef?.current === document.activeElement &&
      event.target?.value?.length > 2
    ) {
      setLoadingAutoComplete(true);
      const filtros = {
        nomeRazaoSocial: event.target.value,
        apelidoFantasia: event.target.value,
      };
      timeoutBuscaAutocomplete(buscar?.(filtros));
    }
  };

  const onChangeMultipleVendedores = (valores) => {
    if (!valores?.length) {
      resetarFiltrosChave("vendedores");
      return;
    }
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      int_vendedor_id: valores.map((valor) => valor.id),
      vendedores: valores.map(({ id, nomeRazaoSocial }) => ({
        id,
        nomeRazaoSocial,
      })),
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      Vendedores: valores
        .map(({ nomeRazaoSocial }) => nomeRazaoSocial)
        .join(" | "),
    }));
    setFilteredVendedorList(
      vendedorList.filter(
        (vendedor) => !valores.map(({ id }) => id).includes(vendedor.id),
      ),
    );
  };

  const onChangeMultiplePrestadores = (valores) => {
    if (!valores?.length) {
      resetarFiltrosChave("prestadores");
      return;
    }
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      int_prestador_id: valores.map((valor) => valor.id),
      prestadores: valores.map(({ id, nomeRazaoSocial }) => ({
        id,
        nomeRazaoSocial,
      })),
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      Prestadores: valores
        .map(({ nomeRazaoSocial }) => nomeRazaoSocial)
        .join(" | "),
    }));
    setFilteredPrestadorList(
      prestadorList.filter(
        (prestador) => !valores.map(({ id }) => id).includes(prestador.id),
      ),
    );
  };

  function resetarFiltrosChave(chaveFiltro) {
    let chaveFiltroImpressao = chaveFiltro;
    switch (chaveFiltro) {
      case "status":
        setFiltrosAvancados((prevState) => {
          const prevStateTemp = { ...prevState };
          delete prevStateTemp.status;
          delete prevStateTemp.statusPersonalizadoId;
          return prevStateTemp;
        });
        setFiltrosAplicadosImpressao((prevState) => {
          const prevStateTemp = { ...prevState };
          delete prevStateTemp.status;
          delete prevStateTemp.statusPersonalizadoId;
          return prevStateTemp;
        });
        break;
      case "vendedores":
        setFilteredVendedorList(vendedorList);
        chaveFiltroImpressao = "Vendedores";
        break;
      case "prestadores":
        setFilteredPrestadorList(prestadorList);
        chaveFiltroImpressao = "Prestadores";
        break;
      case "ativoCampoChave":
        chaveFiltroImpressao = "Campo-Chave";
        break;
      case "ativoId":
        setFiltrosAvancados((prevState) => {
          const prevStateTemp = { ...prevState };
          delete prevStateTemp.ativoCampoChave;
          return prevStateTemp;
        });
        setFiltrosAplicadosImpressao((prevState) => {
          const prevStateTemp = { ...prevState };
          delete prevStateTemp["Campo-Chave"];
          return prevStateTemp;
        });
        chaveFiltroImpressao = "Ativo";
        break;
      default:
        break;
    }
    setFiltrosAvancados((prevState) => {
      const prevStateTemp = { ...prevState };
      delete prevStateTemp[chaveFiltro];
      return prevStateTemp;
    });
    setFiltrosAplicadosImpressao((prevState) => {
      const prevStateTemp = { ...prevState };
      delete prevStateTemp[chaveFiltroImpressao];
      return prevStateTemp;
    });
  }

  function onChangeMultipleEmpresas(empresas) {
    if (empresas.length === 0) {
      return;
    }
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      empresasIds: empresas.map(({ id }) => id),
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      "Empresas-Associadas": empresas
        .map(({ razaoSocial }) => razaoSocial)
        .join(" | "),
    }));
  }

  function handleChangeFiltroCanceladas(e) {
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      canceladas: e.target.value,
    }));
    if (e.target.value !== "null") {
      setFiltrosAplicadosImpressao((prevState) => ({
        ...prevState,
        ["OS-Canceladas"]: e.target.value === "true" ? "Sim" : "Não",
      }));
    } else {
      const filtrosImpressao = { ...filtrosAplicadosImpressao };
      delete filtrosImpressao["OS-Canceladas"];
      setFiltrosAplicadosImpressao(filtrosImpressao);
    }
  }

  const buscarStatusPersonalizados = async function () {
    const result = await ordemServicosStatusPersonalizadosService.getAll();
    if (!result.isAxiosError) {
      const colunasCompletas = [
        { id: null, descricao: "ABERTO", cor: "#4FBB53" },
        ...result.data,
        { id: null, descricao: "FECHADO", cor: "#004780" },
      ];
      const statusListCompleto = colunasCompletas.map((col) => ({
        ...col,
        id: col.id,
        descricao: col.descricao,
      }));
      setStatusList(statusListCompleto);
    } else {
      responseErros(result);
    }
  };

  return (
    <>
      {usuario?.acessosEmpresas?.length > 0 && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="empresasIds"
                name="empresasIds"
                options={usuario.acessosEmpresas}
                noOptionsText="Sem opções"
                autoHighlight
                disablePortal
                filterSelectedOptions
                getOptionLabel={(option) => option?.razaoSocial || ""}
                onChange={(_, newValue) => onChangeMultipleEmpresas(newValue)}
                value={value.multiple(
                  usuario.acessosEmpresas || [],
                  filtrosAvancados["empresasIds"]
                    ? filtrosAvancados["empresasIds"]
                    : "",
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    required
                    label="Empresas"
                    margin="normal"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
        </>
      )}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            id="numeroDocumento"
            name="Número-Documento"
            label="Nº Documento"
            variant="outlined"
            margin="normal"
            fullWidth
            onChange={(e) => onChangeInput(e, "Número-Documento")}
            value={value.text(filtrosAvancados?.numeroDocumento)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            id="serieDocumento"
            name="Serie-Documento"
            label="Série Documento"
            variant="outlined"
            margin="normal"
            fullWidth
            onChange={(e) => onChangeInput(e, "Serie-Documento")}
            value={value.text(filtrosAvancados?.serieDocumento)}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="clienteId"
              name="Cliente"
              options={clienteList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.nomeRazaoSocial ?? ""}
              getOptionSelected={(option, value) =>
                option?.nomeRazaoSocial === value?.nomeRazaoSocial
              }
              loading={loadingAutoComplete}
              loadingText="Carregando"
              noOptionsText="Digite e pressione Enter"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente"
                  variant="outlined"
                  onKeyDown={(e) => onInputChangePessoa(e, 1)}
                  inputRef={inputRefCliente}
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              onChange={(e, value) =>
                onChangeAutocomplete("clienteId", value, "Cliente")
              }
              value={value.autoComplete(
                clienteList,
                filtrosAvancados?.clienteId,
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              multiple
              id="vendedores"
              name="Vendedores"
              options={filteredVendedorList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.nomeRazaoSocial ?? ""}
              getOptionSelected={(option, value) =>
                option?.nomeRazaoSocial === value?.nomeRazaoSocial
              }
              loading={loadingAutoComplete}
              loadingText="Carregando"
              noOptionsText="Digite e pressione Enter"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vendedores"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              onChange={(_, newValue) => {
                onChangeMultipleVendedores(newValue);
              }}
              value={filtrosAvancados?.vendedores || []}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              multiple
              id="prestadores"
              name="Prestadores"
              options={filteredPrestadorList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.nomeRazaoSocial ?? ""}
              getOptionSelected={(option, value) =>
                option?.nomeRazaoSocial === value?.nomeRazaoSocial
              }
              loading={loadingAutoComplete}
              loadingText="Carregando"
              noOptionsText="Digite e pressione Enter"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Prestadores"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              onChange={(_, newValue) => {
                onChangeMultiplePrestadores(newValue);
              }}
              value={filtrosAvancados?.prestadores || []}
            />
          </FormControl>
        </Grid>
      </Grid>
      {ativoList.length > 0 && (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl variant="outlined" margin="normal" fullWidth>
              <Autocomplete
                id="ativoId"
                name="Ativo"
                options={ativoList}
                autoHighlight
                disablePortal
                getOptionLabel={(option) => option?.nome ?? ""}
                getOptionSelected={(option, value) => option?.id === value?.id}
                noOptionsText="Sem opções"
                renderInput={(params) => (
                  <TextField {...params} label="Ativo" variant="outlined" />
                )}
                onChange={(_, newValue) => onChangeAtivo(newValue)}
                value={value.autoComplete(ativoList, filtrosAvancados?.ativoId)}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            {campoChaveAtivo?.type === "enum" ? (
              <Select
                id="ativoCampoChave"
                name="ativoCampoChave"
                label={campoChaveAtivo.label}
                margin="normal"
                fullWidth
                onChange={(event) =>
                  onChangeCampoChaveAtivo(event.target.value)
                }
                value={value.text(filtrosAvancados?.ativoCampoChave)}
                SelectProps={{
                  MenuProps: {
                    disablePortal: true,
                    PaperProps: { sx: { zIndex: 1600 } },
                  },
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                {(campoChaveAtivo.values || "")
                  .split(",")
                  .filter((item) => item)
                  .map((item, index) => (
                    <MenuItem key={index} value={item}>
                      {item}
                    </MenuItem>
                  ))}
              </Select>
            ) : (
              <TextField
                id="ativoCampoChave"
                name="Campo-Chave"
                label={campoChaveAtivo?.label || "Campo Chave"}
                variant="outlined"
                margin="normal"
                fullWidth
                disabled={!ativoSelecionado}
                onChange={(e) => onChangeCampoChaveAtivo(e.target.value)}
                value={value.text(filtrosAvancados?.ativoCampoChave)}
              />
            )}
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataLancamentoStart"
            name="Data-Lançamento-De"
            label="Data Lançamento De"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataLancamentoStart)}
            onChange={(date) =>
              onChangeDate("dataLancamentoStart", date, "Data-Lançamento-De")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataLancamentoEnd"
            name="Data-Lançamento-Até"
            label="Data Lançamento Até"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataLancamentoEnd)}
            onChange={(date) =>
              onChangeDate("dataLancamentoEnd", date, "Data-Lançamento-Até")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataFechamentoStart"
            name="Data-Fechamento-De"
            label="Data Fechamento De"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataFechamentoStart)}
            onChange={(date) =>
              onChangeDate("dataFechamentoStart", date, "Data-Fechamento-De")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataFechamentoEnd"
            name="Data-Fechamento-Até"
            label="Data Fechamento Até"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataFechamentoEnd)}
            onChange={(date) =>
              onChangeDate("dataFechamentoEnd", date, "Data-Fechamento-Até")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Select
            id="status"
            name="status"
            label="Status"
            margin="normal"
            fullWidth
            onChange={(event) => onChangeSelectStatus(event, "Status")}
            value={value.text(
              filtrosAvancados?.status ||
                filtrosAvancados?.statusPersonalizadoId,
            )}
            SelectProps={{
              MenuProps: {
                disablePortal: true,
                PaperProps: {
                  sx: {
                    zIndex: 1600,
                  },
                },
              },
            }}
          >
            {statusList.map((status, index) => (
              <MenuItem key={index} value={status.id || status.descricao}>
                <i
                  className="ph-fill ph-circle"
                  style={{ color: `${status.cor}` }}
                ></i>
                &nbsp;{status.descricao}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          className="d-flex justify-content-evenly align-items-center mt-2"
        >
          <FormControl>
            <RadioGroup
              row
              aria-labelledby="row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              value={filtrosAvancados?.canceladas || "false"}
              onChange={handleChangeFiltroCanceladas}
            >
              <FormControlLabel
                value={"true"}
                control={<Radio />}
                label="Canceladas"
              />
              <FormControlLabel
                value={"false"}
                control={<Radio sx={{ ml: "2rem" }} />}
                label="Não Canceladas"
              />
              <FormControlLabel
                value={"null"}
                control={<Radio sx={{ ml: "2rem" }} />}
                label="Ambas"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />

      <Grid
        container
        spacing={2}
        className="d-flex justify-content-center align-items-center p-2"
      >
        <Grid item xs={6}>
          <FormControlLabel
            label="Agrupar por Cliente"
            labelPlacement="end"
            control={
              <Checkbox
                name="agruparPorCliente"
                checked={!!filtrosAvancados?.agruparPorCliente}
                onChange={(e) => onChangeCheckbox(e, "Agrupar-Por-Cliente")}
                color="primary"
              />
            }
          />
        </Grid>
        {ativoList.length > 0 && (
          <Grid item xs={6}>
            <FormControlLabel
              label="Agrupar por Ativo (Campo Chave)"
              labelPlacement="end"
              control={
                <Checkbox
                  name="agruparPorAtivo"
                  checked={!!filtrosAvancados?.agruparPorAtivo}
                  onChange={(e) => onChangeCheckbox(e, "Agrupar-Por-Ativo")}
                  color="primary"
                />
              }
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default FormFiltroAvancado;
